import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { Play, X } from "lucide-react";
import { parseYouTubeId, youtubeEmbedUrl, youtubeThumbnail } from "@/lib/youtube";

type TrailerContextValue = {
  openId: string | null;
  open: (id: string) => void;
  close: () => void;
};

const TrailerContext = createContext<TrailerContextValue | null>(null);

export function TrailerController({ children }: { children: React.ReactNode }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const open = useCallback((id: string) => setOpenId(id), []);
  const close = useCallback(() => setOpenId(null), []);
  return (
    <TrailerContext.Provider value={{ openId, open, close }}>{children}</TrailerContext.Provider>
  );
}

function useTrailer() {
  const ctx = useContext(TrailerContext);
  if (!ctx) throw new Error("TrailerSection must be used within TrailerController");
  return ctx;
}

type Props = {
  filmId: string;
  filmTitle: string;
  trailerUrl: string | null | undefined;
};

export function TrailerSection({ filmId, filmTitle, trailerUrl }: Props) {
  const videoId = parseYouTubeId(trailerUrl ?? null);
  const { openId, open, close } = useTrailer();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelId = `trailer-panel-${filmId}`;
  const buttonId = `trailer-button-${filmId}`;
  const isOpen = openId === filmId;
  const uid = useId();

  // Escape closes an open trailer and returns focus.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
        buttonRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close]);

  if (!videoId) return null;

  return (
    <>
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => (isOpen ? close() : open(filmId))}
        className="group inline-flex items-center gap-2 rounded-full border border-gold/50 bg-transparent px-5 py-2.5 font-mono-cinema text-[11px] uppercase tracking-[0.2em] text-gold transition-all duration-200 hover:bg-gold/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full border border-gold/60 transition-colors group-hover:bg-gold group-hover:text-night-deep">
          {isOpen ? <X className="h-3 w-3" aria-hidden /> : <Play className="h-3 w-3 translate-x-[1px]" aria-hidden />}
        </span>
        {isOpen ? "Sluit trailer" : "Bekijk trailer"}
      </button>

      <section
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!isOpen}
        className="mt-6 w-full basis-full md:mt-8"
      >
        {isOpen && (
          <div
            key={uid}
            className="relative overflow-hidden rounded-2xl border border-border/60 bg-night-deep/60 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.6)] motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-[.99] motion-safe:duration-200"
          >
            <button
              type="button"
              onClick={() => {
                close();
                buttonRef.current?.focus();
              }}
              aria-label={`Sluit trailer voor ${filmTitle}`}
              className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-night-deep/80 text-foreground/90 backdrop-blur-sm transition-colors hover:bg-night-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
            <div className="aspect-video w-full">
              <iframe
                src={youtubeEmbedUrl(videoId, { autoplay: true })}
                title={`Trailer — ${filmTitle}`}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="h-full w-full border-0"
              />
            </div>
          </div>
        )}
      </section>
    </>
  );
}

/**
 * Static preview of the trailer button (no controller/panel). Used inside the
 * admin FilmPreviewDialog to show that the button will appear.
 */
export function TrailerButtonPreview() {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 font-mono-cinema text-[11px] uppercase tracking-[0.2em] text-gold">
      <span className="flex h-6 w-6 items-center justify-center rounded-full border border-gold/60">
        <Play className="h-3 w-3 translate-x-[1px]" aria-hidden />
      </span>
      Bekijk trailer
    </span>
  );
}

export { youtubeThumbnail };